import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { environment } from 'environments/environment';

import { DocSubmitTypeEnum, InvoiceHeader } from '@/model/entities/entity-model';
import { InvoiceStatusCode } from '@/model/enums/invoice-status-code';
import { DbSaveService } from '@/shared/db-save.service';
import { DialogWrapperService } from '@/shared/dialog-wrapper.service';
import { EmailService } from '@/shared/email.service';
import { PaymentService } from '@/shared/payment.service';
import { PrintFns } from '@/shared/print-fns';
import { QbPostResult } from '@/shared/qb-post-result';
import { StringFns } from '@/shared/string-fns';
import { UtilFns } from '@/shared/util-fns';
import { ToastrService } from 'ngx-toastr';

export type StepStatus = 'n/a' | 'pending' | 'in-progress' | 'success' | 'error';

export interface InvoicePostAction {
  invoiceHeader: InvoiceHeader;
  deliveryMethod: DocSubmitTypeEnum;
  shouldSaveToPdf: boolean;
  isManifested: boolean;
  manifestName: string | undefined;
  isAccountOwned: boolean;
  hasPCard: boolean;
  amount: number;

  deliverStatus: StepStatus;
  savePdfStatus: StepStatus;
  pCardStatus: StepStatus;
  postQbStatus: StepStatus;

  overallStatus: 'pending' | 'in-progress' | 'success' | 'error';
  errorMessage: string | null;
  processedTs: Date | null;

  subordinateActions: InvoicePostAction[];
}

export interface SavePdfParams {
  title: string;
  pdfUrl: string;
  headerLeft?: string;
  folderNames: string[];
}

/**
 * Shared processing pipeline for InvoiceHeader end-of-day actions. Both the
 * End-of-Day (Print/Post Invoices) page and the Account-Owned Invoices page
 * call into these Core methods; orchestration (per-row, batch, freight-entry)
 * lives in the calling component.
 */
@Injectable({ providedIn: 'root' })
export class InvoiceProcessingService {
  constructor(
    private http: HttpClient,
    private emailService: EmailService,
    private paymentService: PaymentService,
    private dbSaveService: DbSaveService,
    private dialogService: DialogWrapperService,
    private toastr: ToastrService,
  ) {}

  // -- Print/email side --

  shouldPrint(action: InvoicePostAction): boolean {
    if (action.isManifested) return false;
    return action.deliveryMethod === DocSubmitTypeEnum.Print
        || action.deliveryMethod === DocSubmitTypeEnum.PrintAndEmail;
  }

  shouldEmail(action: InvoicePostAction): boolean {
    if (action.isManifested) return false;
    return action.deliveryMethod === DocSubmitTypeEnum.Email
        || action.deliveryMethod === DocSubmitTypeEnum.PrintAndEmail;
  }

  buildPrintUrl(invoice: InvoiceHeader, includeAll = false): string {
    const allParam = includeAll ? ';all=true' : '';
    return environment.apiRoot + 'api/pdf/pdffromurl/Invoice' + invoice.id
      + '?url=' + UtilFns.getBaseUrl() + 'printable-invoice/' + invoice.id + allParam;
  }

  getDeliveredByString(action: InvoicePostAction): string {
    if (action.isManifested) return 'Manifest';
    switch (action.deliveryMethod) {
      case DocSubmitTypeEnum.Print: return 'Print';
      case DocSubmitTypeEnum.Email: return 'Email';
      case DocSubmitTypeEnum.PrintAndEmail: return 'Print and Email';
      case DocSubmitTypeEnum.None: return 'None';
      default: return '';
    }
  }

  // -- Core steps --

  /**
   * Deliver via print and/or email. `includeAll` renders primary + subordinates
   * as one document. `useSilentPrint` routes print through QZ Tray (batch path);
   * false falls back to window.open (single-row path — intentional failsafe if
   * QZ ever breaks).
   */
  async deliverInvoiceCore(
    action: InvoicePostAction,
    includeAll = false,
    useSilentPrint = false,
    refreshFn?: () => void,
  ): Promise<void> {
    action.deliverStatus = 'in-progress';
    refreshFn?.();

    const allActions = [action, ...action.subordinateActions];

    if (this.shouldPrint(action)) {
      const printableInvoices = allActions.map((a) => a.invoiceHeader);
      const anyAlreadyPrinted = printableInvoices.some((i) => !!i.printedAt);
      if (!anyAlreadyPrinted) {
        const pdfUrl = this.buildPrintUrl(action.invoiceHeader, includeAll);
        if (useSilentPrint) {
          await this.silentPrintViaQz(pdfUrl, 'Invoice' + action.invoiceHeader.id);
        } else {
          window.open(pdfUrl, '_blank');
        }
        const now = new Date();
        printableInvoices.forEach((i) => (i.printedAt = now));
      }
    }

    for (const a of allActions) {
      if (this.shouldEmail(a)) {
        const emailAddr = a.invoiceHeader.joHeader.billingEmail?.trim()
                       || a.invoiceHeader.joHeader.account?.invoiceEmail?.trim();
        if (emailAddr && UtilFns.validateMultipleEmail(emailAddr)) {
          const status = await this.emailService.sendInvoiceEmail(a.invoiceHeader.id, emailAddr);
          if (status !== 'accepted' && status !== 'stored') {
            throw new Error(`Email delivery failed for ${a.invoiceHeader.id}: ${status}`);
          }
        }
      }
    }

    action.deliverStatus = 'success';
  }

  /**
   * Push the invoice's PDF to the account's drive folder. Idempotent via
   * InvoiceHeader.pdfSavedAt.
   */
  async savePdfCore(action: InvoicePostAction, refreshFn?: () => void): Promise<void> {
    action.savePdfStatus = 'in-progress';
    refreshFn?.();

    if (action.invoiceHeader.pdfSavedAt) {
      action.savePdfStatus = 'success';
      return;
    }

    const accountName = StringFns.stripInvalidFilenameChars(action.invoiceHeader.joHeader.account.accountName);
    const folderName = StringFns.stripInvalidFilenameChars(action.invoiceHeader.joHeader.portalName || 'Default');

    const body: SavePdfParams = {
      title: 'Invoice' + action.invoiceHeader.id,
      pdfUrl: UtilFns.getBaseUrl() + 'printable-invoice/' + action.invoiceHeader.id + ';all=true',
      folderNames: [accountName, folderName],
    };
    const url = environment.apiRoot + 'api/pdf/SavePdfToDrive';
    await this.dbSaveService.postData<string>(url, body, { responseType: 'text' as 'json' });

    action.invoiceHeader.pdfSavedAt = new Date();
    action.savePdfStatus = 'success';
  }

  /**
   * Charge the account's P-Card for `action.amount` via Authorize.Net.
   * Skips if the invoice already has a pCardAmt (idempotent).
   */
  async chargePCardCore(action: InvoicePostAction, refreshFn?: () => void): Promise<void> {
    action.pCardStatus = 'in-progress';
    refreshFn?.();

    const joh = action.invoiceHeader.joHeader;
    const invh = action.invoiceHeader;
    if (!joh.pCardCustomerProfileId || !joh.pCardPaymentProfileId || invh.pCardAmt) {
      action.pCardStatus = 'n/a';
      return;
    }

    let result: any;
    try {
      result = await this.paymentService.submitPayment(
        joh.pCardCustomerProfileId,
        joh.pCardPaymentProfileId,
        action.amount,
        action.invoiceHeader.joHeader.id.toString(),
      );
    } catch (err: any) {
      const msg = this.paymentService.getErrorMessage(err);
      await this.dialogService.okDialog('P-Card Charge Failed', msg);
      throw new Error(msg);
    }

    if (result?.transactionResponse?.transId) {
      invh.pCardAmt = action.amount;
      invh.pCardTransactionId = result.transactionResponse.transId;
      await this.dbSaveService.saveChanges();
      action.pCardStatus = 'success';
    } else {
      const msg = this.paymentService.getErrorMessage(result);
      await this.dialogService.okDialog('P-Card Charge Failed', msg);
      throw new Error(msg);
    }
  }

  /**
   * POST the invoice to QBO. Routes to the account-owned endpoint when
   * `action.isAccountOwned`. Idempotent via invoice status / GLInvoiceId.
   */
  async postToQbCore(action: InvoicePostAction, refreshFn?: () => void): Promise<void> {
    action.postQbStatus = 'in-progress';
    refreshFn?.();

    const invh = action.invoiceHeader;
    if (invh.invoiceStatusId === InvoiceStatusCode.InvoicePosted || !!invh.gLInvoiceId) {
      action.postQbStatus = 'success';
      return;
    }

    const endpoint = action.isAccountOwned ? 'PostAccountOwnedInvoiceToGL' : 'PostInvoiceToGL';
    const url = `${environment.apiRoot}api/quickbooks/${endpoint}?invoiceHeaderId=${invh.id}`;
    const result = await firstValueFrom(this.http.post<QbPostResult>(url, {}).pipe(timeout(60000)));

    const posted = result?.PostedIds ?? [];
    const failed = result?.Failed ?? [];

    if (failed.length > 0 || posted.length === 0) {
      const failedIds = failed.length > 0
        ? failed.map((f) => f.EntityId).join(', ')
        : action.invoiceHeader.id;
      const firstMsg = failed[0]?.ErrorMessage
        || 'QuickBooks did not post the invoice. Check the QuickBooks Issues page or the server log.';
      this.toastr.error(
        `Failed to post invoice(s) ${failedIds} to QuickBooks: ${firstMsg}`,
        'QuickBooks'
      );
      throw new Error(`QB post failed for ${failedIds}: ${firstMsg}`);
    }

    this.toastr.success(
      `Posted ${posted.length} invoice${posted.length === 1 ? '' : 's'} to QuickBooks`,
      'QuickBooks'
    );
    action.postQbStatus = 'success';
  }

  // -- Print helpers --

  private async silentPrintViaQz(pdfUrl: string, name: string): Promise<void> {
    const sp = PrintFns.getSelectedPrinters();
    if (!sp || !sp.invoicePrinter) {
      throw new Error('Batch silent print requires an Invoice Printer to be configured under Printer Setup.');
    }
    const started = await PrintFns.startQZ();
    if (!started) {
      throw new Error('QZ Tray is not running on this workstation. Batch silent print requires QZ Tray.');
    }
    // Pre-fetch the PDF as a Blob so QZ Tray's JVM doesn't fetch the URL itself —
    // the dev cert on https://localhost:7222 fails JVM PKIX validation.
    const blob = await firstValueFrom(this.http.get(pdfUrl, { responseType: 'blob' }));
    const base64 = await this.blobToBase64(blob);
    await PrintFns.printFiles([{ url: pdfUrl, name, data: base64 as any }], 'invoicePrinter');
  }

  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const comma = result.indexOf(',');
        resolve(comma >= 0 ? result.substring(comma + 1) : result);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  }
}
